/**
 * Real-time metrics component.
 * Connects to MetricsHub and renders near real-time activity updates.
 */

import { useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, Chip, Typography } from '@mui/material';
import type { RealTimeMetricsDto } from '../../types/dashboard';
import { metricsSignalRService } from '../../services/metricsSignalRService';
import { useAuthStore } from '../../store/authStore';
import { useContextStore } from '../../store/contextStore';
import { useSignalR } from '../../providers/SignalRProvider';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 6 }).format(value);

const formatNumber = (value: number) => new Intl.NumberFormat('en-US').format(value);

/**
 * Real-time metrics widget.
 */
export const RealTimeMetrics = () => {
  const { metricsStatus } = useSignalR();
  const [metrics, setMetrics] = useState<RealTimeMetricsDto | null>(null);
  const token = useAuthStore((s) => s.token);
  const tenantId = useContextStore((s) => s.currentTenant?.id ?? '');

  useEffect(() => {
    metricsSignalRService.on({
      onRealTimeMetricsUpdated: (data) => {
        setMetrics(data);
      },
    });

    return () => {
      metricsSignalRService.off();
    };
  }, []);

  const statusChip = (() => {
    const status: ConnectionStatus = metricsStatus;
    if (status === 'connected') return <Chip size="small" color="success" label="Live" />;
    if (status === 'connecting') return <Chip size="small" color="warning" label="Connecting" />;
    return <Chip size="small" color="default" label="Offline" />;
  })();

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Real-time Activity
          </Typography>
          {statusChip}
        </Box>

        {!token && (
          <Alert severity="info">
            Sign in to enable real-time metrics.
          </Alert>
        )}

        {token && !tenantId && (
          <Alert severity="info">
            Select a tenant (and project) to enable real-time metrics.
          </Alert>
        )}

        {tenantId && !metrics && (
          <Typography variant="body2" color="text.secondary">
            Waiting for updates…
          </Typography>
        )}

        {metrics && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 2 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Active sessions
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {formatNumber(metrics.activeSessions)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Executions (last minute)
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {formatNumber(metrics.executionsLastMinute)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Tokens (last minute)
              </Typography>
              <Typography variant="h5" fontWeight={600}>
                {formatNumber(metrics.tokensLastMinute)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Cost (last minute)
              </Typography>
              <Typography variant="h5" fontWeight={600} color="primary">
                {formatCurrency(metrics.costLastMinute)}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

